import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Clock, Loader2, X } from 'lucide-react';
import { uploadStage } from './invoiceStatus';
import { useInvoicesBase } from './basePath';

const ICONS = {
  uploading: Loader2, uploaded: Clock, queued: Clock, reading: Loader2, failed: AlertTriangle, removed: X,
  needs_review: AlertTriangle, awaiting_approval: Clock, auto: CheckCircle2, approved: CheckCircle2, rejected: X,
};

const STEPS = ['uploaded', 'reading', 'done'];

function stepIndex(key) {
  if (key === 'uploading' || key === 'uploaded' || key === 'queued') return 0;
  if (key === 'reading') return 1;
  return 2;
}

export default function UploadStatusList({ uploads, invoicesByDoc, progress = {}, now = Date.now(), onDismiss }) {
  const base = useInvoicesBase();
  if (!uploads.length) return null;
  return (
    <ul className="space-y-2" data-testid="upload-status-list" aria-live="polite">
      {uploads.map((u) => {
        const st = uploadStage(u, invoicesByDoc, now, progress);
        const Icon = ICONS[st.key] || Clock;
        const spin = st.key === 'reading' || st.key === 'uploading';
        const failed = st.key === 'failed';
        const at = stepIndex(st.key);
        return (
          <li key={u.key} data-testid={`upload-row-${u.key}`}
            className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 bg-surface ${failed ? 'border-danger/40' : 'border-surface-border'}`}>
            <Icon className={`w-5 h-5 shrink-0 ${failed ? 'text-danger' : 'text-primary-500'} ${spin ? 'animate-spin' : ''}`} aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-ink truncate">{u.name}</div>
              <div className="flex items-center gap-1.5 mt-1" aria-hidden="true">
                {STEPS.map((s, i) => (
                  <span key={s} className={`h-1.5 w-8 rounded-full ${failed ? 'bg-danger/30' : i <= at ? 'bg-primary-400' : 'bg-surface-sunken'}`} />
                ))}
              </div>
              {st.detail && <div className="text-xs mt-1 text-ink-muted" data-testid={`upload-detail-${u.key}`}>{st.detail}</div>}
              {st.reason && <div className={`text-xs mt-1 ${failed ? 'text-danger' : 'text-ink-muted'}`} data-testid={`upload-reason-${u.key}`}>{st.reason}</div>}
            </div>
            <span data-testid={`upload-status-${u.key}`} className={`text-sm font-medium ${failed ? 'text-danger' : 'text-ink'}`}>{st.label}</span>
            {st.invoiceId && (
              <Link to={`${base}/${st.invoiceId}`} data-testid={`upload-open-${u.key}`}
                className="inline-flex items-center min-h-[44px] px-4 rounded-lg bg-primary-500 text-surface text-sm font-medium hover:bg-primary-600">
                Open
              </Link>
            )}
            {onDismiss && (
              <button type="button" onClick={() => onDismiss(u.key)} aria-label={`Hide ${u.name}`}
                data-testid={`upload-dismiss-${u.key}`} className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg text-ink-muted hover:bg-surface-sunken">
                <X className="w-4 h-4" />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
